/**
 * Deployments — Superadmin view of build SHA and deployment age across all environments.
 * Polls the /api/health endpoint of each service every 30 seconds.
 *
 * Route: /admin/deployments
 */
import React, { useEffect, useState, useCallback } from "react";

const GITHUB_REPO = "velaro/velaro-admin";

interface BuildInfo {
  sha: string;
  builtAt: string;
  shortSha: string;
  ageMinutes: number | null;
}

interface HealthResponse {
  service?: string;
  status: string;
  timestamp: string;
  build?: BuildInfo;
}

interface EnvCard {
  label: string;
  service: "admin" | "messaging";
  url: string;
  env: "staging" | "production";
}

const ENVIRONMENTS: EnvCard[] = [
  {
    label: "Staging — Admin",
    service: "admin",
    env: "staging",
    url: "https://velaro-admin-staging.azurewebsites.net/api/health"
  },
  {
    label: "Staging — Messaging",
    service: "messaging",
    env: "staging",
    url: "https://velaro-messaging-api-staging.azurewebsites.net/api/health"
  },
  {
    label: "Production — Admin",
    service: "admin",
    env: "production",
    url: "https://velaro-admin-prod.azurewebsites.net/api/health"
  },
  {
    label: "Production — Messaging",
    service: "messaging",
    env: "production",
    url: "https://velaro-messaging-api.azurewebsites.net/api/health"
  }
];

const GITHUB_REPOS: Record<"admin" | "messaging", string> = {
  admin: "velaro/velaro-admin",
  messaging: "velaro/velaro-messaging"
};

const REFRESH_INTERVAL_MS = 30_000;

interface CardState {
  loading: boolean;
  error: string | null;
  data: HealthResponse | null;
  lastChecked: Date | null;
}

function formatRelative(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return `${Math.floor(diffHrs / 24)}d ago`;
}

function formatAge(minutes: number | null | undefined): string {
  if (minutes == null) return "unknown";
  if (minutes < 60) return `${minutes}m`;
  const hrs = Math.floor(minutes / 60);
  if (hrs < 24) return `${hrs}h ${minutes % 60}m`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
}

function formatAbsolute(iso: string): string {
  if (!iso || iso === "unknown") return "unknown";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function StatusBadge({ status }: { status: string }) {
  const base = "inline-block px-2 py-0.5 rounded text-xs font-semibold";
  if (status === "Healthy") {
    return <span className={`${base} bg-green-100 text-green-800`}>Healthy</span>;
  }
  if (status === "Degraded") {
    return <span className={`${base} bg-yellow-100 text-yellow-800`}>Degraded</span>;
  }
  if (status === "Critical") {
    return <span className={`${base} bg-red-100 text-red-800`}>Critical</span>;
  }
  return <span className={`${base} bg-gray-100 text-gray-600`}>{status}</span>;
}

function EnvCardView({ card, state }: { card: EnvCard; state: CardState }) {
  const isProd = card.env === "production";
  const borderColor = isProd ? "border-blue-200" : "border-gray-200";
  const headerBg = isProd ? "bg-blue-50" : "bg-gray-50";

  const repoName = GITHUB_REPOS[card.service];
  const sha = state.data?.build?.sha;
  const shortSha = state.data?.build?.shortSha;
  const builtAt = state.data?.build?.builtAt;
  const ageMinutes = state.data?.build?.ageMinutes;

  const commitUrl =
    sha && sha !== "dev"
      ? `https://github.com/${repoName}/commit/${sha}`
      : null;

  return (
    <div className={`border ${borderColor} rounded-lg overflow-hidden shadow-sm`}>
      <div className={`${headerBg} px-4 py-3 flex items-center justify-between`}>
        <span className="font-semibold text-sm text-gray-800">{card.label}</span>
        {state.loading && !state.data ? (
          <span className="text-xs text-gray-400">Loading...</span>
        ) : state.data ? (
          <StatusBadge status={state.data.status} />
        ) : (
          <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-500">
            Unreachable
          </span>
        )}
      </div>

      <div className="px-4 py-3 space-y-2 text-sm">
        {state.error && !state.data && (
          <p className="text-red-600 text-xs">{state.error}</p>
        )}

        {state.data?.build ? (
          <>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 w-20 shrink-0">Commit</span>
              {commitUrl ? (
                <a
                  href={commitUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-blue-600 hover:underline"
                >
                  {shortSha ?? "—"}
                </a>
              ) : (
                <span className="font-mono text-gray-400">{shortSha ?? "dev"}</span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-gray-500 w-20 shrink-0">Built</span>
              <span className="text-gray-700">
                {builtAt && builtAt !== "unknown"
                  ? `${formatAge(ageMinutes)} ago — ${formatAbsolute(builtAt)}`
                  : "unknown"}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-gray-500 w-20 shrink-0">Age</span>
              <span className="text-gray-700">{formatAge(ageMinutes)}</span>
            </div>
          </>
        ) : state.data ? (
          <p className="text-gray-400 text-xs italic">
            Build info not available (deploy before this feature shipped)
          </p>
        ) : null}

        {state.lastChecked && (
          <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
            <span className="text-gray-400 w-20 shrink-0 text-xs">Checked</span>
            <span className="text-gray-400 text-xs">{formatRelative(state.lastChecked)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Deployments() {
  const [cards, setCards] = useState<CardState[]>(
    ENVIRONMENTS.map(() => ({ loading: true, error: null, data: null, lastChecked: null }))
  );
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL_MS / 1000);

  const fetchAll = useCallback(async () => {
    setCards(prev => prev.map(c => ({ ...c, loading: true })));

    const results = await Promise.allSettled(
      ENVIRONMENTS.map(async (env) => {
        const resp = await fetch(env.url, {
          signal: AbortSignal.timeout(10_000)
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return (await resp.json()) as HealthResponse;
      })
    );

    const now = new Date();
    setCards(
      results.map((result) => {
        if (result.status === "fulfilled") {
          return {
            loading: false,
            error: null,
            data: result.value,
            lastChecked: now
          };
        } else {
          return {
            loading: false,
            error: String((result as PromiseRejectedResult).reason),
            data: null,
            lastChecked: now
          };
        }
      })
    );
    setLastRefresh(now);
    setCountdown(REFRESH_INTERVAL_MS / 1000);
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Auto-refresh timer
  useEffect(() => {
    const interval = setInterval(fetchAll, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchAll]);

  // Countdown display
  useEffect(() => {
    const tick = setInterval(() => {
      setCountdown(prev => (prev <= 1 ? REFRESH_INTERVAL_MS / 1000 : prev - 1));
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  const stagingCards = cards.slice(0, 2);
  const prodCards = cards.slice(2);
  const stagingEnvs = ENVIRONMENTS.slice(0, 2);
  const prodEnvs = ENVIRONMENTS.slice(2);

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Deployments</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Build SHA and age for each environment. Auto-refreshes every 30s.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">
            Refreshing in {countdown}s
            {lastRefresh && ` · Last: ${formatRelative(lastRefresh)}`}
          </span>
          <button
            onClick={fetchAll}
            className="text-xs px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 text-gray-600"
          >
            Refresh now
          </button>
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Staging
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stagingEnvs.map((env, i) => (
            <EnvCardView key={env.url} card={env} state={stagingCards[i]} />
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Production
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {prodEnvs.map((env, i) => (
            <EnvCardView key={env.url} card={env} state={prodCards[i]} />
          ))}
        </div>
      </div>
    </div>
  );
}
